export interface SoundExample {
  kab: string;
  phonetic: string;
  fr: string;
  icon: string;
  note?: string;
}

export interface KabyleSound {
  id: string;
  letter: string;
  uppercase: string;
  tifinagh: string;
  ipa: string;
  soundName: string;
  category: 'special' | 'emphatic' | 'vowel' | 'guttural' | 'affricate';
  categoryLabel: string;
  description: string;
  mouthGuide: string;
  commonMistake: string;
  tip: string;
  examples: SoundExample[];
}

export interface PronunciationRule {
  id: string;
  title: string;
  icon: string;
  badge: string;
  summary: string;
  explanation: string;
  keyPoints: string[];
  examples: {
    kab: string;
    phonetic: string;
    fr: string;
    highlight: string;
  }[];
}

export interface PracticalExpression {
  id: string;
  category: 'SALUTATIONS' | 'POLITESSE' | 'MAISON_REPAS' | 'VILLAGE_NATURE' | 'SAGESSE';
  categoryLabel: string;
  categoryIcon: string;
  kab: string;
  phonetic: string;
  fr: string;
  icon: string;
  context: string;
  culturalNote?: string;
}

export interface PronunciationQuizItem {
  id: string;
  audioText: string;
  question: string;
  correctAnswer: string;
  options: {
    label: string;
    phonetic: string;
    meaning: string;
  }[];
  explanation: string;
}

// ============================================================================
// 1. LES SONS & LETTRES DU KABYLE
// ============================================================================
export const KABYLE_SOUNDS: KabyleSound[] = [
  {
    id: 'sound_gh',
    letter: 'ɣ',
    uppercase: 'Ɣ',
    tifinagh: 'ⵗ',
    ipa: '[ɣ] ou [ʁ]',
    soundName: 'Le R Grasseyé / Guttural (Gh)',
    category: 'guttural',
    categoryLabel: 'Sons Gutturaux',
    description: 'Son équivalent au "r" grasseyé du français standard (comme dans "Paris", "Rose") ou au "ghayn" (غ) arabe.',
    mouthGuide: 'Faites vibrer l’arrière de la langue contre le voile du palais sans rouler la pointe de la langue.',
    commonMistake: 'Confondre avec le "R" roulé espagnol / berbère (r / ṛ).',
    tip: 'Pensez au "r" français bien prononcé dans la gorge.',
    examples: [
      { kab: 'Aɣrum', phonetic: 'A-rhroum', fr: 'Le pain traditionnel / la galette', icon: '🍞', note: 'Aliment sacré du foyer' },
      { kab: 'Iɣil', phonetic: 'I-rhil', fr: 'Le bras / la force', icon: '💪', note: 'Symbole de courage' },
      { kab: 'Taɣat', phonetic: 'Ta-rhat', fr: 'La chèvre de la montagne', icon: '🐐', note: 'Animal protecteur' },
      { kab: 'Iɣzer', phonetic: 'I-rhzer', fr: 'Le ravin / le torrent', icon: '🏞️', note: 'Paysage montagnard' }
    ]
  },
  {
    id: 'sound_ayn',
    letter: 'ɛ',
    uppercase: 'Ɛ',
    tifinagh: 'ⵄ',
    ipa: '[ʕ]',
    soundName: 'Le A Pharyngal Profond (Ayn)',
    category: 'guttural',
    categoryLabel: 'Sons Gutturaux',
    description: 'Consonne pharyngale sonore émise du fond de la gorge, identique au "Ayn" (ع) en arabe.',
    mouthGuide: 'Resserrez légèrement les muscles de la gorge et émettez un son "a" profond en faisant vibrer les cordes vocales.',
    commonMistake: 'Le prononcer comme un simple "e" ou "a" français sans tension dans la gorge.',
    tip: 'Imaginez que vous prenez une inspiration profonde avant d’expulser un "a" depuis le larynx.',
    examples: [
      { kab: 'Aɛrur', phonetic: 'A-a-rour', fr: 'Le dos / l’échine', icon: '🏋️', note: 'Symbole de résistance' },
      { kab: 'Tasaɛett', phonetic: 'Ta-sa-att', fr: 'L’heure / la montre', icon: '⏱️', note: 'Mesure du temps' },
      { kab: 'Tamurt', phonetic: 'Ta-mourt', fr: 'La terre / le pays', icon: '🏔️', note: 'La patrie ancestrale' },
      { kab: 'Ɛass', phonetic: 'A-ass', fr: 'Garder / surveiller', icon: '🛡️', note: 'Vigilance du guetteur' }
    ]
  },
  {
    id: 'sound_ch',
    letter: 'c',
    uppercase: 'C',
    tifinagh: 'ⵛ',
    ipa: '[ʃ]',
    soundName: 'Le son "Ch"',
    category: 'special',
    categoryLabel: 'Sons Spécifiques',
    description: 'Se prononce toujours "CH" comme dans "Chien", "Château" ou l’anglais "Shop".',
    mouthGuide: 'Avancez légèrement les lèvres en rond et laissez passer l’air sur le dessus de la langue sans faire vibrer la gorge.',
    commonMistake: 'Le prononcer "K" ou "S" comme la lettre "C" en français.',
    tip: 'En alphabet kabyle, la lettre "c" se lit TOUJOURS "ch" !',
    examples: [
      { kab: 'Amcic', phonetic: 'Am-chich', fr: 'Le chat du village', icon: '🐱', note: 'Félin familier' },
      { kab: 'Ccetwa', phonetic: 'Ch-chet-wa', fr: 'L’hiver froid et neigeux', icon: '❄️', note: 'Saison des veillées' },
      { kab: 'Taqcict', phonetic: 'Takh-chicht', fr: 'La jeune fille', icon: '👧', note: 'Féminin régulier' },
      { kab: 'Aqcic', phonetic: 'Akh-chich', fr: 'Le jeune garçon', icon: '👦', note: 'Masculin régulier' }
    ]
  },
  {
    id: 'sound_tch',
    letter: 'č',
    uppercase: 'Č',
    tifinagh: 'ⵞ',
    ipa: '[tʃ]',
    soundName: 'Le son "Tch"',
    category: 'affricate',
    categoryLabel: 'Consonnes Affriquées',
    description: 'Son affriqué composé : démarre par un "T" sec et s’enchaîne immédiatement avec un "CH" (comme dans "Tchèque" ou "Match").',
    mouthGuide: 'Bloquez l’air brièvement avec la pointe de la langue derrière les dents du haut, puis relâchez en soufflant un "ch".',
    commonMistake: 'Oublier l’attaque en "T" et ne prononcer qu’un simple "ch".',
    tip: 'Pensez au bruit du train "Tchou-tchou" ou au mot "Tchad".',
    examples: [
      { kab: 'Ecc', phonetic: 'Etch', fr: 'Manger / se nourrir', icon: '🍲', note: 'Verbe indispensable' },
      { kab: 'Iččur', phonetic: 'Itch-tchour', fr: 'C’est plein / rempli', icon: '🏺', note: 'La jarre est pleine' },
      { kab: 'Aččir', phonetic: 'Atch-tchir', fr: 'La branche verte', icon: '🌿', note: 'Nature montagnarde' }
    ]
  },
  {
    id: 'sound_dj',
    letter: 'ǧ',
    uppercase: 'Ǧ',
    tifinagh: 'ⴵ',
    ipa: '[dʒ]',
    soundName: 'Le son "Dj"',
    category: 'affricate',
    categoryLabel: 'Consonnes Affriquées',
    description: 'Son doux et vibrant composé d’un "D" suivi d’un "J" (comme dans "Djellaba", "Django" ou l’anglais "Job").',
    mouthGuide: 'La pointe de la langue touche le palais pour le "D" puis laisse passer l’air voisé en "J".',
    commonMistake: 'Le prononcer comme un simple "G" dur de "Gâteau".',
    tip: 'Pensez au "J" du mot "Jazz" ou "Djinn".',
    examples: [
      { kab: 'Ajeǧǧig', phonetic: 'A-jedj-djig', fr: 'La fleur épanouie', icon: '🌸', note: 'Symbole de renouveau' },
      { kab: 'Tajeǧǧigt', phonetic: 'Ta-jedj-djigt', fr: 'La petite fleur', icon: '🌼', note: 'Prénom féminin très doux' },
      { kab: 'Ǧǧet-iyi', phonetic: 'Djedj-tet i-yi', fr: 'Laissez-moi / permettez-moi', icon: '✋', note: 'Formule de politesse' }
    ]
  },
  {
    id: 'sound_ha',
    letter: 'ḥ',
    uppercase: 'Ḥ',
    tifinagh: 'ⵃ',
    ipa: '[ħ]',
    soundName: 'Le H Aspiré Chaud (Hha)',
    category: 'guttural',
    categoryLabel: 'Sons Gutturaux',
    description: 'Consonne pharyngale sourde très expressive, identique au "Haa" (ح) en arabe.',
    mouthGuide: 'Ouvrez la bouche et expirez un souffle chaud depuis le milieu de la gorge, comme si vous vouliez embuer un miroir ou des lunettes.',
    commonMistake: 'Laisser la lettre muette comme en français ou la prononcer comme un "R".',
    tip: 'Faites comme si vous souffliez sur vos mains en hiver pour les réchauffer : "Hhh" !',
    examples: [
      { kab: 'Aḥeddadh', phonetic: 'A-hhed-dadh', fr: 'Le forgeron du village', icon: '🔨', note: 'Maître du fer et du feu' },
      { kab: 'Aḥbib', phonetic: 'A-hhbib', fr: 'Le véritable ami', icon: '🤝', note: 'Valeur de fraternité' },
      { kab: 'Tafukt', phonetic: 'Ta-foukt', fr: 'La chaleur bienfaisante', icon: '☀️', note: 'Rayon de soleil' }
    ]
  },
  {
    id: 'sound_ou',
    letter: 'u',
    uppercase: 'U',
    tifinagh: 'ⵓ',
    ipa: '[u]',
    soundName: 'La voyelle "OU"',
    category: 'vowel',
    categoryLabel: 'Voyelles Fondamentales',
    description: 'Se prononce TOUJOURS "OU" (comme dans "Loup", "Roue", "Bijou"). Il n’y a pas de son "U" français en Kabyle.',
    mouthGuide: 'Arrondissez les lèvres en avant et produisez un son "ou" pur et chantant.',
    commonMistake: 'Prononcer "u" comme dans "mur" ou "lune".',
    tip: 'Dès que vous voyez un "u" en kabyle, lisez instantanément "OU" !',
    examples: [
      { kab: 'Ul', phonetic: 'Oul', fr: 'Le cœur / les sentiments', icon: '❤️', note: 'Centre des émotions' },
      { kab: 'Uccen', phonetic: 'Ouch-chen', fr: 'Le chacal rusé', icon: '🐺', note: 'Héros des contes kabyles' },
      { kab: 'Tudert', phonetic: 'Tou-dert', fr: 'La vie et l’existence', icon: '🌱', note: '« Tudert d asafu »' },
      { kab: 'Axxam', phonetic: 'Akh-kham', fr: 'La maison traditionnelle', icon: '🏡', note: 'Le sanctuaire familial' }
    ]
  },
  {
    id: 'sound_z_emphatic',
    letter: 'ẓ',
    uppercase: 'Ẓ',
    tifinagh: 'ⵣ',
    ipa: '[zˤ]',
    soundName: 'Le Z Emphatique (Aza)',
    category: 'emphatic',
    categoryLabel: 'Consonnes Emphatiques',
    description: 'Le "Z" amazigh par excellence (le symbole ⵣ de l’Homme libre), prononcé lourd et grave.',
    mouthGuide: 'Placez la langue à plat contre le palais tout en reculant sa racine, en créant une caisse de résonance grave.',
    commonMistake: 'Le prononcer comme un simple "z" léger de "zèbre".',
    tip: 'Donnez de la profondeur et de la fierté à la vibration sonore.',
    examples: [
      { kab: 'Aẓar', phonetic: 'A-zzar', fr: 'La racine / l’origine', icon: '🌳', note: 'Les racines ancestrales' },
      { kab: 'Iẓri', phonetic: 'I-zzri', fr: 'La vue / la larme émue', icon: '👁️', note: 'Regard profond' },
      { kab: 'Ẓeṭṭ', phonetic: 'Zzett', fr: 'Tisser un tapis de laine', icon: '🧵', note: 'Art des femmes kabyles' }
    ]
  },
  {
    id: 'sound_d_emphatic',
    letter: 'ḍ',
    uppercase: 'Ḍ',
    tifinagh: 'ⴹ',
    ipa: '[dˤ]',
    soundName: 'Le D Emphatique / Grave',
    category: 'emphatic',
    categoryLabel: 'Consonnes Emphatiques',
    description: 'Un "D" prononcé avec le dos de la langue relevé vers le voile du palais, donnant un son lourd et percutant.',
    mouthGuide: 'Appuyez fermement le plat de la langue contre les incisives et le palais supérieur.',
    commonMistake: 'Le prononcer comme un "D" léger sans résonance.',
    tip: 'Faites résonner le son dans toute la cavité buccale.',
    examples: [
      { kab: 'Aḍar', phonetic: 'A-ddhar', fr: 'Le pied / la jambe', icon: '🦶', note: 'Pour gravir les sentiers' },
      { kab: 'Iḍ', phonetic: 'Idh', fr: 'La nuit étoilée', icon: '🌙', note: 'Moment du repos' },
      { kab: 'Semmiḍ', phonetic: 'Sem-midh', fr: 'Le froid mordant des cimes', icon: '❄️', note: 'Hiver en montagne' }
    ]
  },
  {
    id: 'sound_t_emphatic',
    letter: 'ṭ',
    uppercase: 'Ṭ',
    tifinagh: 'ⵟ',
    ipa: '[tˤ]',
    soundName: 'Le T Emphatique / Percutant',
    category: 'emphatic',
    categoryLabel: 'Consonnes Emphatiques',
    description: 'Un "T" explosif et profond, distinct du "T" classique doux.',
    mouthGuide: 'Bloquez l’air fermement avec la langue élargie contre le palais, puis relâchez avec netteté.',
    commonMistake: 'Le confondre avec le son "TH" anglais.',
    tip: 'Un son franc, net et retentissant comme un claquement.',
    examples: [
      { kab: 'Iṭij', phonetic: 'I-ttij', fr: 'Le soleil étincelant', icon: '☀️', note: 'Source de lumière' },
      { kab: 'Taṭuct', phonetic: 'Ta-ttoucht', fr: 'L’hirondelle messagère', icon: '🕊️', note: 'Annonce le printemps' },
      { kab: 'Ṭṭes', phonetic: 'Ttes', fr: 'Dors / repose-toi', icon: '😴', note: 'Impératif du coucher' }
    ]
  },
  {
    id: 'sound_kh',
    letter: 'x',
    uppercase: 'X',
    tifinagh: 'ⵅ',
    ipa: '[x] ou [χ]',
    soundName: 'Le son "Kh" (Jota / Bach)',
    category: 'guttural',
    categoryLabel: 'Sons Gutturaux',
    description: 'Son rauque et raclé de la gorge, identique au "Kha" (خ) arabe, à la "jota" espagnole ou au "ch" allemand dans "Bach".',
    mouthGuide: 'Rapprochez l’arrière de la langue du palais mou et faites frotter l’air sans faire vibrer les cordes vocales.',
    commonMistake: 'Le prononcer "KS" comme la lettre "X" en français ("taxi").',
    tip: 'En kabyle, la lettre "X" se lit TOUJOURS "KH" !',
    examples: [
      { kab: 'Axxam', phonetic: 'Akh-kham', fr: 'La maison / le foyer', icon: '🏡', note: 'Le toit familial' },
      { kab: 'Ixef', phonetic: 'I-khef', fr: 'La tête / le sommet', icon: '🧠', note: 'La pensée' },
      { kab: 'Xdem', phonetic: 'Kh-dem', fr: 'Travailler / agir avec zèle', icon: '🛠️', note: 'Valeur du labeur' }
    ]
  },
  {
    id: 'sound_q',
    letter: 'q',
    uppercase: 'Q',
    tifinagh: 'ⵇ',
    ipa: '[q]',
    soundName: 'Le Q Uvulaire Profond',
    category: 'guttural',
    categoryLabel: 'Sons Gutturaux',
    description: 'Un "K" prononcé tout au fond de la gorge au niveau de la luette, identique au "Qaf" (ق) arabe.',
    mouthGuide: 'Frappez l’arrière extrême de la langue contre la luette (uvule). C’est un son sec et profond.',
    commonMistake: 'Le prononcer comme un "K" français trop en avant de la bouche.',
    tip: 'Pensez à un son de goutte d’eau tombant dans un puits profond.',
    examples: [
      { kab: 'Aqjun', phonetic: 'Ak-joun', fr: 'Le chien fidèle de berger', icon: '🐕', note: 'Gardien du troupeau' },
      { kab: 'Qim', phonetic: 'Kim', fr: 'Assieds-toi / reste avec nous', icon: '🪑', note: 'Hospitalité' },
      { kab: 'Aqqur', phonetic: 'Ak-kour', fr: 'La sécheresse / la dureté', icon: '🪵', note: 'Résistance du chêne' }
    ]
  },
  {
    id: 'sound_e_chva',
    letter: 'e',
    uppercase: 'E',
    tifinagh: 'ⴻ',
    ipa: '[ə]',
    soundName: 'Le Chva Neutre (Voyelle de liaison)',
    category: 'vowel',
    categoryLabel: 'Voyelles Fondamentales',
    description: 'Une voyelle très courte et neutre (comme le "e" dans "le", "je" ou l’anglais "the"), qui sert à lier les consonnes.',
    mouthGuide: 'Bouche détendue, son très court et sans accentuation.',
    commonMistake: 'L’accentuer en "É" ou "È". Elle ne doit jamais être accentuée.',
    tip: 'C’est une voyelle fluide qui aide à prononcer des suites de 3 consonnes sans effort.',
    examples: [
      { kab: 'Medden', phonetic: 'Med-den', fr: 'Les gens / la communauté', icon: '👥', note: 'L’ensemble du village' },
      { kab: 'Targit', phonetic: 'Tar-guit', fr: 'Le rêve / l’espérance', icon: '✨', note: 'Vision poétique' },
      { kab: 'Dder', phonetic: 'D-der', fr: 'Vivre et persévérer', icon: '🌱', note: 'Verbe d’existence' }
    ]
  }
];

// ============================================================================
// 2. RÈGLES FONDAMENTALES DE PRONONCIATION
// ============================================================================
export const PRONUNCIATION_RULES: PronunciationRule[] = [
  {
    id: 'rule_vowel_u',
    title: 'La Règle d’Or du "U" = Toujours "OU"',
    icon: '✨',
    badge: 'Essentiel',
    summary: 'La lettre "U" se lit systématiquement comme "OU" en français, sans aucune exception.',
    explanation: 'En linguistique amazighe, le son "U" français ([y]) n’existe pas. Dès que vous voyez un "u", prononcez un "ou" chaleureux et rond.',
    keyPoints: [
      'Ul se prononce "Oul" (et non "Ul")',
      'Tudert se prononce "Tou-dert"',
      'Axxam comporte deux "a" et un "m", la syllabe reste fluide.'
    ],
    examples: [
      { kab: 'Ul', phonetic: 'Oul', fr: 'Le cœur', highlight: 'U -> OU' },
      { kab: 'Uccen', phonetic: 'Ouch-chen', fr: 'Le chacal', highlight: 'U -> OU, cc -> CH-CH' },
      { kab: 'Tudert', phonetic: 'Tou-dert', fr: 'La vie', highlight: 'u -> ou' },
      { kab: 'Tala', phonetic: 'Ta-la', fr: 'La fontaine', highlight: 'a -> a ouvert' }
    ]
  },
  {
    id: 'rule_feminine_tt',
    title: 'Le Cadre Féminin "T...T"',
    icon: '👧',
    badge: 'Grammaire & Son',
    summary: 'La grande majorité des noms féminins commencent ET se terminent par la lettre "T".',
    explanation: 'Le "T" initial marque le féminin, et le "T" final clôture le mot. À l’oral, le T final est doux mais nettement articulé.',
    keyPoints: [
      'Axxam (La maison) -> Taxxamt (La petite chambre / pièce)',
      'Adrar (La montagne) -> Tadrart (La colline)',
      'Ajeǧǧig (La fleur) -> Tajeǧǧigt (La petite fleur)'
    ],
    examples: [
      { kab: 'Axxam', phonetic: 'Akh-kham', fr: 'Maison (Masc.)', highlight: 'Début en A' },
      { kab: 'Taxxamt', phonetic: 'Takh-khamt', fr: 'Chambre (Fém.)', highlight: 'Encadré T...T' },
      { kab: 'Adrar', phonetic: 'Adh-rar', fr: 'Montagne (Masc.)', highlight: 'Début en A' },
      { kab: 'Tadrart', phonetic: 'Tadh-rart', fr: 'Colline (Fém.)', highlight: 'Encadré T...T' }
    ]
  },
  {
    id: 'rule_gemination',
    title: 'La Gémination (Lettres doubles = Temps d’arrêt)',
    icon: '⚡',
    badge: 'Rythme & Force',
    summary: 'Quand une consonne est doublée (ex: "kk", "tt", "dd", "qq"), on insiste sur la prononciation avec une pause d’un demi-temps.',
    explanation: 'La consonne double change souvent le sens du mot ou renforce l’action. Marquez une légère pression avant de relâcher le son.',
    keyPoints: [
      'Iqqim : marquer l’arrêt sur le "qq" (Ik-kime)',
      'Iččur : marquer l’intensité sur le "čč" (Itch-tchour)',
      'Medden : insister sur le "dd" pour bien cadencer le mot.'
    ],
    examples: [
      { kab: 'Iqqim', phonetic: 'Ik-kime', fr: 'Il s’est assis', highlight: 'qq = arrêt sec' },
      { kab: 'Iččur', phonetic: 'Itch-tchour', fr: 'Il est rempli', highlight: 'čč = double tch' },
      { kab: 'Ajeǧǧig', phonetic: 'A-jedj-djig', fr: 'La fleur', highlight: 'ǧǧ = double dj' }
    ]
  },
  {
    id: 'rule_chva',
    title: 'Le "E" Neutre (Lubrifiant de consonnes)',
    icon: '💧',
    badge: 'Fluidité',
    summary: 'La lettre "E" n’est jamais accentuée : elle sert de respiration légère entre les groupes de consonnes.',
    explanation: 'Le kabyle aime les consonnes puissantes. Le "e" permet de passer d’une consonne à une autre en douceur sans hacher la phrase.',
    keyPoints: [
      'Ne dites jamais "É" ou "È"',
      'Prononcez un "e" très court et léger comme dans le mot français "re-partir"',
      'Il apporte la musicalité poétique aux vers kabyles.'
    ],
    examples: [
      { kab: 'Medden', phonetic: 'Med-den', fr: 'Les gens', highlight: 'e neutre' },
      { kab: 'Targit', phonetic: 'Tar-guit', fr: 'Le rêve', highlight: 'e bref' },
      { kab: 'Ecc', phonetic: 'Etch', fr: 'Manger', highlight: 'e d’attaque' }
    ]
  }
];

// ============================================================================
// 3. PHRASES & EXPRESSIONS PRATIQUES DU QUOTIDIEN
// ============================================================================
export const PRACTICAL_EXPRESSIONS: PracticalExpression[] = [
  // SALUTATIONS
  {
    id: 'exp_azul',
    category: 'SALUTATIONS',
    categoryLabel: 'Salutations & Rencontres',
    categoryIcon: '👋',
    kab: 'Azul fell-awen !',
    phonetic: 'A-zoul fel-la-wen !',
    fr: 'Bonjour à toutes et à tous !',
    icon: '🌞',
    context: 'La salutation universelle amazighe chaleureuse adressée à un groupe.',
    culturalNote: '« Azul » vient de « Azar » (racine) et « Ul » (cœur) : saluer du fond du cœur.'
  },
  {
    id: 'exp_amek',
    category: 'SALUTATIONS',
    categoryLabel: 'Salutations & Rencontres',
    categoryIcon: '👋',
    kab: 'Amek i tellid ?',
    phonetic: 'A-mek i tel-lidh ?',
    fr: 'Comment vas-tu ?',
    icon: '💬',
    context: 'Demander des nouvelles à un ami ou à un proche rencontré sur le chemin.',
    culturalNote: 'Formule incontournable au début de toute discussion.'
  },
  {
    id: 'exp_labas',
    category: 'SALUTATIONS',
    categoryLabel: 'Salutations & Rencontres',
    categoryIcon: '👋',
    kab: 'Labas, tanmirt !',
    phonetic: 'La-bas, tan-mirt !',
    fr: 'Tout va très bien, merci !',
    icon: '😊',
    context: 'Réponse positive et polie à « Amek i tellid ? ».'
  },
  {
    id: 'exp_ar_tufat',
    category: 'SALUTATIONS',
    categoryLabel: 'Salutations & Rencontres',
    categoryIcon: '👋',
    kab: 'Ar tufat !',
    phonetic: 'Ar tou-fat !',
    fr: 'Au revoir / À la prochaine rencontre !',
    icon: '🌅',
    context: 'Prendre congé en souhaitant de se retrouver sous de meilleurs auspices.',
    culturalNote: 'Littéralement : « Jusqu’à ce que nous nous retrouvions dans la lumière ».'
  },

  // POLITESSE
  {
    id: 'exp_ansuf',
    category: 'POLITESSE',
    categoryLabel: 'Hospitalité & Politesse',
    categoryIcon: '🤝',
    kab: 'Ansuf yis-wen !',
    phonetic: 'An-souf yis-wen !',
    fr: 'Soyez les très bienvenus parmi nous !',
    icon: '🏡',
    context: 'Accueillir des invités au seuil de la maison kabyle.',
    culturalNote: 'L’hospitalité (Inebgi n Rebbi) est sacrée dans tous les villages.'
  },
  {
    id: 'exp_ssurfet',
    category: 'POLITESSE',
    categoryLabel: 'Hospitalité & Politesse',
    categoryIcon: '🤝',
    kab: 'Ssurfet-iyi...',
    phonetic: 'S-sour-fet i-yi...',
    fr: 'Pardonnez-moi / Excusez-moi...',
    icon: '🙏',
    context: 'S’excuser courtoisement ou demander la parole dans une assemblée.'
  },
  {
    id: 'exp_tanmirt',
    category: 'POLITESSE',
    categoryLabel: 'Hospitalité & Politesse',
    categoryIcon: '🤝',
    kab: 'Tanmirt tameqrant !',
    phonetic: 'Tan-mirt ta-mek-rant !',
    fr: 'Un immense merci du fond du cœur !',
    icon: '💖',
    context: 'Remercier avec gratitude après une aide ou un service rendu.'
  },

  // MAISON ET REPAS
  {
    id: 'exp_ecc_tezmert',
    category: 'MAISON_REPAS',
    categoryLabel: 'Maison & Repas',
    categoryIcon: '🍲',
    kab: 'Ecc s tezmert-ik !',
    phonetic: 'Etch s tez-mert-ik !',
    fr: 'Bon appétit ! (Mange en pleine santé)',
    icon: '🥣',
    context: 'Souhaiter un bon repas à la personne qui partage la galette et le couscous.',
    culturalNote: '« Tazmert » signifie la santé, la force et la vigueur.'
  },
  {
    id: 'exp_awi_aman',
    category: 'MAISON_REPAS',
    categoryLabel: 'Maison & Repas',
    categoryIcon: '🍲',
    kab: 'Awi-d cwiṭ n waman.',
    phonetic: 'A-wi-d chwitt n wa-man.',
    fr: 'Apporte un peu d’eau fraîche de la source.',
    icon: '💧',
    context: 'Demander de l’eau à table ou après une marche sous le soleil.'
  },
  {
    id: 'exp_lkanun',
    category: 'MAISON_REPAS',
    categoryLabel: 'Maison & Repas',
    categoryIcon: '🍲',
    kab: 'Qim ɣer lkanun.',
    phonetic: 'Kim rher lka-noun.',
    fr: 'Assieds-toi près du feu de bois.',
    icon: '🔥',
    context: 'Inviter un voyageur frileux à se réchauffer près des braises d’olivier.'
  },

  // VILLAGE ET NATURE
  {
    id: 'exp_taddart_cbahe',
    category: 'VILLAGE_NATURE',
    categoryLabel: 'Village & Nature',
    categoryIcon: '🏔️',
    kab: 'Taddart-agi tucbeḥ aṭas !',
    phonetic: 'Tad-dart a-gui touch-beh a-thas !',
    fr: 'Ce village sur la crête est magnifique !',
    icon: '🏘️',
    context: 'Contempler les maisons en pierre perchées sur les cimes du Djurdjura.'
  },
  {
    id: 'exp_tala_aman',
    category: 'VILLAGE_NATURE',
    categoryLabel: 'Village & Nature',
    categoryIcon: '🏔️',
    kab: 'Netteddu ɣer Tala.',
    phonetic: 'Net-ted-dou rher Ta-la.',
    fr: 'Nous allons à la fontaine du village.',
    icon: '⛲',
    context: 'La marche quotidienne vers la source d’eau pure.'
  },
  {
    id: 'exp_adrar_lalla',
    category: 'VILLAGE_NATURE',
    categoryLabel: 'Village & Nature',
    categoryIcon: '🏔️',
    kab: 'Adrar n Djurdjura d aɛlayen.',
    phonetic: 'Adh-rar n Djour-djou-ra d a-a-la-yen.',
    fr: 'Le mont Djurdjura est majestueux et élevé.',
    icon: '⛰️',
    context: 'Évocation des crêtes rocheuses enneigées.'
  },

  // SAGESSE & PROVERBES
  {
    id: 'exp_afus_deg_ufus',
    category: 'SAGESSE',
    categoryLabel: 'Sagesse & Proverbes',
    categoryIcon: '📜',
    kab: 'Afus deg ufus !',
    phonetic: 'A-fous deg ou-fous !',
    fr: 'La main dans la main (L’union et la solidarité Tiwizi).',
    icon: '🤝',
    context: 'Devise ancestrale lors des travaux collectifs d’entraide au village.',
    culturalNote: 'Tiwizi désigne le travail bénévole collectif où tout le village aide un voisin.'
  },
  {
    id: 'exp_aman_d_tudert',
    category: 'SAGESSE',
    categoryLabel: 'Sagesse & Proverbes',
    categoryIcon: '📜',
    kab: 'Aman d tudert.',
    phonetic: 'A-man d tou-dert.',
    fr: 'L’eau, c’est la vie.',
    icon: '💧',
    context: 'Respect sacré voué aux sources et aux rivières en Kabylie.'
  },
  {
    id: 'exp_tilelli',
    category: 'SAGESSE',
    categoryLabel: 'Sagesse & Proverbes',
    categoryIcon: '📜',
    kab: 'Tilelli d asalu n tdukli.',
    phonetic: 'Ti-lel-li d a-sa-lou n tdouk-li.',
    fr: 'La Liberté est le socle de l’unité.',
    icon: '🦅',
    context: 'Chant poétique célébrant la dignité et la liberté.'
  }
];

// ============================================================================
// 4. QUESTIONS DU TEST D'ÉCOUTE & PRONONCIATION (QUIZ)
// ============================================================================
export const PRONUNCIATION_QUIZ: PronunciationQuizItem[] = [
  {
    id: 'quiz_1',
    audioText: 'Aɣrum',
    question: 'Écoutez attentivement. Quel est ce mot et que signifie-t-il ?',
    correctAnswer: 'Aɣrum',
    options: [
      { label: 'Aɣrum', phonetic: 'A-rhroum', meaning: 'Le pain traditionnel' },
      { label: 'Axxam', phonetic: 'Akh-kham', meaning: 'La maison familiale' },
      { label: 'Amcic', phonetic: 'Am-chich', meaning: 'Le chat de ferme' }
    ],
    explanation: 'Le mot prononcé comporte la lettre « ɣ » (R grasseyé / Gh) : A-ɣrum, le pain kabyle cuit sur le tajine.'
  },
  {
    id: 'quiz_2',
    audioText: 'Azul fell-awen',
    question: 'Écoutez cette célèbre expression. Que signifie-t-elle ?',
    correctAnswer: 'Azul fell-awen',
    options: [
      { label: 'Azul fell-awen', phonetic: 'A-zoul fel-la-wen', meaning: 'Bonjour à tous !' },
      { label: 'Ar tufat', phonetic: 'Ar tou-fat', meaning: 'Au revoir / À bientôt' },
      { label: 'Ansuf yis-wen', phonetic: 'An-souf yis-wen', meaning: 'Bienvenue chez nous' }
    ],
    explanation: '« Azul fell-awen » est la salutation fraternelle amazighe (« La paix / bonjour sur vous »).'
  },
  {
    id: 'quiz_3',
    audioText: 'Amcic',
    question: 'Repérez le son « c » (Ch) dans le mot audio :',
    correctAnswer: 'Amcic',
    options: [
      { label: 'Amcic', phonetic: 'Am-chich', meaning: 'Le chat' },
      { label: 'Aydi', phonetic: 'Ay-di', meaning: 'Le chien' },
      { label: 'Iṭij', phonetic: 'I-ttij', meaning: 'Le soleil' }
    ],
    explanation: 'En alphabet kabyle, la lettre « c » se prononce toujours « ch » : Amcic = Am-chich.'
  },
  {
    id: 'quiz_4',
    audioText: 'Ul',
    question: 'Comment se prononce la voyelle « u » dans ce mot essentiel ?',
    correctAnswer: 'Ul',
    options: [
      { label: 'Ul', phonetic: 'Oul', meaning: 'Le cœur (prononcé "Oul")' },
      { label: 'Il', phonetic: 'Il', meaning: 'Prononcé avec un I' },
      { label: 'Al', phonetic: 'Al', meaning: 'Prononcé avec un A' }
    ],
    explanation: 'La lettre « u » se prononce TOUJOURS « OU ». Ul = Oul (Le cœur).'
  },
  {
    id: 'quiz_5',
    audioText: 'Afus deg ufus',
    question: 'Écoutez ce grand proverbe de solidarité :',
    correctAnswer: 'Afus deg ufus',
    options: [
      { label: 'Afus deg ufus', phonetic: 'A-fous deg ou-fous', meaning: 'La main dans la main (Solidarité Tiwizi)' },
      { label: 'Aman d tudert', phonetic: 'A-man d tou-dert', meaning: 'L’eau c’est la vie' },
      { label: 'Ecc s tezmert-ik', phonetic: 'Etch s tez-mert-ik', meaning: 'Bon appétit' }
    ],
    explanation: '« Afus deg ufus » exprime l’entraide et l’union fraternelle montagnarde.'
  }
];
